import React, { useEffect, useMemo, useState } from 'react';
import { BarList, Btn, Chip, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, TextInput, fmtDate, panelPost, useAdminGet } from '../kit';
import { Pager, Pill, S, ExportButtons, list, num, pageInfo, rowsOf, str, useDebounced } from './shared';
import {
  EMPTY_TAG_FILTER, LIFECYCLE_LABEL, TagFilter, TagFilterValue, UserTagCells, describeTagFilter, tagFilterActive,
  tagFilterParams, tagText, toLifecycle, useTagOptions,
} from '../tags/shared';

/**
 * Sample leads — everyone who filled the "download sample reports" form,
 * newest first (GET ip_intel/leads). One row per submission: a visitor who
 * comes back for a second PDF in the same session files a second row with
 * the same details, which is why "unique emails" sits next to the count.
 * Since October 2026 the reports may be e-mailed instead of (or as well as)
 * downloaded: "Sent by" is the mode in force when the form was sent, "E-mail"
 * what happened to the mail, and "Send" e-mails the reports again
 * (POST ip_intel/lead_resend — the visitor caps do not apply to an admin).
 */

const COLS = ['created_at', 'name', 'email', 'lifecycle_tags', 'organisation', 'designation', 'reports', 'delivery_mode',
  'email_status', 'downloads', 'last_download_at', 'ip_country', 'user_id', 'visitor_id'];
const LABELS: Record<string, string> = {
  created_at: 'When', name: 'Name', email: 'Email', lifecycle_tags: 'Lifecycle · tags', organisation: 'Organisation', designation: 'Designation',
  reports: 'Reports', delivery_mode: 'Sent by', email_status: 'E-mail', downloads: 'Downloaded', last_download_at: 'Last download',
  ip_country: 'Country', user_id: 'Account', visitor_id: 'Visitor',
};
const MODE_LABEL: Record<string, string> = { download: 'Download', email: 'E-mail', both: 'Both' };
const EMAIL_STATUS: Record<string, { label: string; tone: 'good' | 'bad' | 'warn' | 'neutral' }> = {
  sent: { label: 'Sent', tone: 'good' }, failed: { label: 'Failed', tone: 'bad' },
  skipped_cap: { label: 'Not sent (cap)', tone: 'warn' }, sending: { label: 'Sending', tone: 'neutral' },
};
const EMAIL_FILTER: Record<string, string> = {
  '': 'Any e-mail status', sent: 'E-mailed', failed: 'E-mail failed', skipped_cap: 'Not sent (cap)', none: 'Not e-mailed',
};

/** The account's lifecycle + tags as text (user_tags.decorate, by the lead's
 *  account id, else its e-mail; a guest with no account has none) — what the
 *  table's own search, column filter and CSV see; the cell renders pills. */
const lifecycleText = (r: any) => {
  const lc = toLifecycle(r?.lifecycle);
  return [r?.lifecycle_label || LIFECYCLE_LABEL[lc] || lc, tagText(r)].filter(Boolean).join(' · ');
};

type Filters = { q: string; report: string; from: string; to: string; email_status: string };
const EMPTY: Filters = { q: '', report: '', from: '', to: '', email_status: '' };

const Leads: React.FC<{ samples: { id: string; title: string }[] }> = ({ samples }) => {
  const [f, setF] = useState<Filters>(EMPTY);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('50');
  const [tick, setTick] = useState(0);
  const [dlErr, setDlErr] = useState('');
  // Send: first click arms the row's button, the second (within 6 s) sends.
  const [armed, setArmed] = useState<string | null>(null);
  const [sending, setSending] = useState<string | null>(null);
  const [sendMsg, setSendMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(null), 6000);
    return () => clearTimeout(t);
  }, [armed]);
  const q = useDebounced(f.q);
  // The lead's account lifecycle + tags (standard params; server-side, and
  // the exports honour them too). A guest lead never matches one.
  const [tagF, setTagF] = useState<TagFilterValue>(EMPTY_TAG_FILTER);
  const tagOpts = useTagOptions();
  const set = (k: keyof Filters) => (v: string) => setF(s => ({ ...s, [k]: v }));

  const filterParams = useMemo(() => ({ q, report: f.report, from: f.from, to: f.to, email_status: f.email_status, ...tagFilterParams(tagF) }),
    [q, f.report, f.from, f.to, f.email_status, tagF]);
  const filterKey = JSON.stringify(filterParams);
  useEffect(() => { setPage(0); }, [filterKey, perPage]);

  const res = useAdminGet(S, 'leads', { ...filterParams, page, per_page: perPage },
    [filterKey, page, perPage, tick]);
  const d = res.data || {};
  const raw = rowsOf(d, 'leads');
  const per = Number(perPage) || 50;
  const { total, pages } = pageInfo(d, raw, per);
  const titleOf = (id: string) => samples.find(s => s.id === id)?.title || id;

  const rows = useMemo(() => raw.map(r => ({
    ...r,
    name: str(r.name) || [r.first_name, r.last_name].filter(Boolean).join(' '),
    reports: list(r.reports),
    downloads: r.downloaded ?? r.downloads ?? r.download_count,
    lifecycle_tags: lifecycleText(r),
  })), [raw]);


  const chips: { label: string; value: string; clear: () => void }[] = [];
  if (f.q) chips.push({ label: 'Search', value: f.q, clear: () => set('q')('') });
  if (f.report) chips.push({ label: 'Report', value: titleOf(f.report), clear: () => set('report')('') });
  if (f.from) chips.push({ label: 'From', value: f.from, clear: () => set('from')('') });
  if (f.to) chips.push({ label: 'To', value: f.to, clear: () => set('to')('') });
  if (f.email_status) chips.push({ label: 'E-mail', value: EMAIL_FILTER[f.email_status] || f.email_status, clear: () => set('email_status')('') });
  if (tagFilterActive(tagF)) chips.push({
    label: 'Lifecycle / tags',
    value: describeTagFilter(tagF, k => tagOpts.tagByKey[k]?.label || k).join(' · '),
    clear: () => setTagF(EMPTY_TAG_FILTER),
  });

  const summary = { unique_emails: d.unique_emails, emailed: d.emailed, not_emailed: d.not_emailed, ...(d.summary || {}) };

  const resend = async (r: any) => {
    const id = str(r.id);
    if (!id || sending) return;
    if (armed !== id) { setArmed(id); setSendMsg(null); return; }
    setArmed(null); setSending(id); setSendMsg(null);
    const { data, error } = await panelPost(S, 'lead_resend', { id: Number(id) });
    setSending(null);
    if (error) { setSendMsg({ kind: 'error', text: `Could not send to ${r.email}: ${error}` }); return; }
    setSendMsg(data?.sent
      ? { kind: 'ok', text: `The reports were e-mailed to ${r.email}.` }
      : { kind: 'error', text: `The e-mail to ${r.email} did not go out${data?.error ? `: ${data.error}` : ''}.` });
    setTick(t => t + 1);
  };
  const byReport = rowsOf(d.by_report).map((x: any) => ({
    label: str(x.label) || titleOf(str(x.report)), value: num(x.count),
  }));
  return (
    <div className="flex flex-col gap-4">
      {(summary.leads !== undefined || d.total !== undefined) && (
        <KPIRow>
          <KPI label="Leads" value={num(summary.leads ?? total).toLocaleString()} hint={chips.length ? 'matching the filters' : 'all time'} />
          {summary.unique_emails !== undefined && <KPI label="Unique emails" value={num(summary.unique_emails).toLocaleString()} hint="all time" />}
          {summary.downloads !== undefined && <KPI label="PDFs downloaded" value={num(summary.downloads).toLocaleString()} />}
          {summary.emailed !== undefined && (
            <KPI label="Samples e-mailed" value={num(summary.emailed).toLocaleString()}
              hint={num(summary.not_emailed) ? `${num(summary.not_emailed).toLocaleString()} not sent · all time` : 'all time'}
              onClick={() => set('email_status')(f.email_status === 'sent' ? '' : 'sent')} active={f.email_status === 'sent'} />
          )}
          {summary.signed_in !== undefined && <KPI label="Signed-in leads" value={num(summary.signed_in).toLocaleString()} />}
        </KPIRow>
      )}
      {byReport.length > 0 && (
        <Section title="Leads by report" right={<span className="text-xs text-grayscale-1100">all time · a lead can ask for several</span>}>
          <BarList items={byReport} />
        </Section>
      )}
      <Section title="Sample-report leads"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-grayscale-1100">{total.toLocaleString()} lead{total === 1 ? '' : 's'}</span>
            {chips.length > 0 && (
              <Btn small onClick={() => { setF(EMPTY); setTagF(EMPTY_TAG_FILTER); }}>
                Clear {chips.length} filter{chips.length === 1 ? '' : 's'}
              </Btn>
            )}
            <Btn small onClick={() => setTick(t => t + 1)} disabled={res.loading}>Refresh</Btn>
            <ExportButtons kind="leads" params={filterParams} onError={setDlErr} what="These leads" />
          </div>
        }>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={f.q} onChange={set('q')} wide placeholder="Search name, email, organisation…" />
          <Select value={f.report} onChange={set('report')} options={['', ...samples.map(s => s.id)]}
            labels={{ '': 'Any report', ...Object.fromEntries(samples.map(s => [s.id, s.title])) }} />
          <span className="text-xs text-grayscale-1100">from</span>
          <TextInput type="date" value={f.from} onChange={set('from')} />
          <span className="text-xs text-grayscale-1100">to</span>
          <TextInput type="date" value={f.to} onChange={set('to')} />
          <Select value={f.email_status} onChange={set('email_status')} options={Object.keys(EMAIL_FILTER)} labels={EMAIL_FILTER} />
        </div>
        <TagFilter value={tagF} onChange={v => setTagF(v)} />
        {chips.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {chips.map(c => <Chip key={c.label} label={c.label} value={c.value} onClear={c.clear} />)}
          </div>
        )}
        {dlErr && <Msg kind="error">Export failed: {dlErr}</Msg>}
        {sendMsg && <Msg kind={sendMsg.kind}>{sendMsg.text}</Msg>}
        {res.error && <Msg kind="error">{res.error}</Msg>}
        {res.loading && !res.data ? <Loading /> : (
          <DataTable rows={rows} columns={COLS} labels={LABELS} maxHeight="40rem"
            empty={chips.length ? 'No leads match these filters.' : 'No sample requests yet.'}
            actions={r => r.email ? (
              <Btn small kind={armed === str(r.id) ? 'primary' : 'subtle'} onClick={() => void resend(r)}
                disabled={!!sending && sending !== str(r.id)}
                title="E-mail the reports this lead asked for (a real e-mail; the visitor caps do not apply)">
                {sending === str(r.id) ? 'Sending…' : armed === str(r.id) ? `Confirm — e-mail ${r.email}` : r.email_status === 'sent' ? 'Resend' : 'E-mail reports'}
              </Btn>
            ) : null}
            render={{
              email: r => r.email
                ? <a href={`mailto:${r.email}`} className="text-primary-700 underline">{r.email}</a> : '—',
              lifecycle_tags: r => <span className="inline-block min-w-[12rem]"><UserTagCells row={r} /></span>,
              reports: r => (
                <span className="inline-flex gap-1">
                  {(r.reports as string[]).length === 0 ? '—' : (r.reports as string[]).map(id => (
                    <span key={id} title={titleOf(id)}
                      className="text-[11px] rounded-full border border-grayscale-300 bg-grayscale-200 px-2 py-0.5 whitespace-nowrap">
                      {id}
                    </span>
                  ))}
                </span>
              ),
              delivery_mode: r => <span className="text-xs whitespace-nowrap">{MODE_LABEL[str(r.delivery_mode)] || str(r.delivery_mode) || 'Download'}</span>,
              email_status: r => {
                const st = EMAIL_STATUS[str(r.email_status)];
                if (!st) return '—';
                const att = r.attached && typeof r.attached === 'object' ? r.attached : null;
                const how = att ? [list(att.attached).length ? `${list(att.attached).length} attached` : '',
                  list(att.linked).length ? `${list(att.linked).length} as links` : ''].filter(Boolean).join(', ') : '';
                const title = [r.emailed_at ? `Sent ${fmtDate(r.emailed_at)}` : '', how, str(r.email_error)].filter(Boolean).join(' · ');
                return <Pill tone={st.tone} title={title || undefined}>{st.label}</Pill>;
              },
              downloads: r => r.downloads === undefined || r.downloads === null ? '—'
                : `${num(r.downloads)} / ${(r.reports as string[]).length}`,
              user_id: r => r.account_email ? <span className="text-xs" title={str(r.user_id)}>{r.account_email}</span>
                : r.user_id ? <span className="font-mono text-[11px]" title={r.user_id}>{str(r.user_id).slice(0, 10)}…</span> : 'guest',
              visitor_id: r => r.visitor_id ? <span className="font-mono text-[11px]" title={r.visitor_id}>{str(r.visitor_id).slice(0, 10)}…</span> : '—',
            }} />
        )}
        {pages > 1 && (
          <Pager page={page} pages={pages} total={total} shown={raw.length} per={per}
            onPage={setPage} perPage={perPage} onPerPage={setPerPage} />
        )}
      </Section>
    </div>
  );
};

export default Leads;
